import {
  Alert,
  Box,
  Button,
  Skeleton,
  Snackbar,
  SvgIcon,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '../../../assets';
import PageArea from '../../../components/shared/PageArea';
import { PageTitleWithButtons } from '../../../components/shared/layout/PageTitleWithButtons';
import { UserContext } from '../../../components/shared/useUser';
import { Section } from '../../../components/ui/Section';
import { DemandModifier } from './types/DemandModifierTypes';
import DemandModifierModal from './components/DemandModifierModal';
import DemandModifiersTable from './components/DemandModifiersTable';
import DeleteDemandModifierDialog from './components/DeleteDemandModifierDialog';
import { client } from '../../../app/services/api/orderManagementClient';
import { useSaveDemandModifier } from './hooks/useSaveDemandModifier';
import useLogError from '../../../hooks/useLogError';

const DemandModifiersPage = () => {
  const { t } = useTranslation('demandModifiers');
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { logError } = useLogError();

  const canWriteDemandModifiers = hasPermissionTo(['BulkOrdersWrite']);
  const canDeleteDemandModifiers = hasPermissionTo(['BulkOrdersDelete']);

  const { saveDemandModifier } = useSaveDemandModifier({ storeNumber: selectedStore?.storeNumber });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modifiers, setModifiers] = useState<DemandModifier[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [selectedModifier, setSelectedModifier] = useState<DemandModifier | null>(null);
  const [modifierToDelete, setModifierToDelete] = useState<DemandModifier | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'error' | 'warning' | 'info' | 'success' }>({ open: false, message: '', severity: 'info' });
  const [skuError, setSkuError] = useState<string>('');
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  useEffect(() => {
    if (selectedStore?.storeNumber) {
      loadModifiers();
    }
  }, [selectedStore]);

  useEffect(() => {
    let cancelled = false;
    const loadCategoriesForSelectedStore = async () => {
      setCategories([]);
      if (!selectedStore?.franchiseUUID) return;

      try {
        const markets = await client.getAccessibleMarkets();
        const market = markets.success && markets.data
          ? markets.data.find(candidate => candidate.uuid === selectedStore.franchiseUUID)
          : undefined;
        if (!market) return;

        const result = await client.getMarketCategories({
          companyId: market.instanceId.toString(),
          pageNumber: 0,
          pageSize: 2147483647,
        });
        if (!cancelled && result.success && result.data?.items) {
          setCategories(result.data.items.map(category => ({ id: category.categoryId, name: category.name })));
        }
      } catch (error) {
        logError(error);
      }
    };

    loadCategoriesForSelectedStore();
    return () => { cancelled = true; };
  }, [selectedStore?.franchiseUUID, logError]);

  const loadModifiers = async () => {
    setIsLoading(true);
    await fetchModifiers();
    setIsLoading(false);
  };

  const fetchModifiers = async () => {
    try {
      if (!selectedStore?.storeNumber) {
        setModifiers([]);
        return;
      }

      const result = await client.getDemandModifiers(selectedStore.storeNumber, false);
      if (result.success) {
        setModifiers(result.data);
      } else {
        setModifiers([]);
      }
    } catch (error) {
      logError(error);
      setModifiers([]);
    }
  };

  const handleCreateModifier = () => {
    setSelectedModifier(null);
    setIsModalOpen(true);
  };

  const handleEditModifier = async (modifier: DemandModifier) => {
    if (!selectedStore?.storeNumber || !modifier.id) {
      setSelectedModifier(modifier);
      setIsModalOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      const result = await client.getDemandModifierById(selectedStore.storeNumber, modifier.id);
      if (result.success && result.data) {
        setSelectedModifier(result.data);
      } else {
        setSelectedModifier(modifier);
      }
    } catch (error) {
      logError(error);
      setSelectedModifier(modifier);
    } finally {
      setIsLoading(false);
    }
    setIsModalOpen(true);
  };

  const handleDeleteClick = (modifier: DemandModifier) => {
    setModifierToDelete(modifier);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveModifier = async (modifier: DemandModifier) => {
    try {
      if (!selectedStore?.storeNumber) return;
      const result = await saveDemandModifier(modifier);
      if (!result.success) {
        if (
          modifier.targetType === 'specificSKUs' &&
          result.message?.includes('do not exist')
        ) {
          setSkuError(result.message);
        } else {
          handleSnackbarOpen(result.message || t('saveError'), 'error');
        }
        return;
      }
      await loadModifiers();
      setIsModalOpen(false);
      setSelectedModifier(null);
      if (result.message) {
        handleSnackbarOpen(result.message, 'warning');
      } else {
        handleSnackbarOpen(modifier.id ? t('modifierUpdated') : t('modifierCreated'), 'success');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('saveError'), 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (modifierToDelete?.id) {
      setIsDeleting(true);
      try {
        if (!selectedStore?.storeNumber) {
          setIsDeleting(false);
          return;
        }

        await client.deleteDemandModifier(selectedStore.storeNumber, modifierToDelete.id);
        await loadModifiers();
      } catch (error) {
        logError(error);
      }
      setIsDeleteDialogOpen(false);
      setModifierToDelete(null);
      setIsDeleting(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedModifier(null);
    setSkuError('');
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setModifierToDelete(null);
  };

 

  return (
    <PageArea>
      <PageTitleWithButtons
        title={t('title')}
        subTitle={t('subtitle')}>
        {canWriteDemandModifiers && (
          <Button
            variant='primary'
            size='lg'
            sx={{ ml: 4 }}
            onClick={handleCreateModifier}
            startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}>
            {t('createButton')}
          </Button>
        )}
      </PageTitleWithButtons>

      {isLoading ? (
        <Skeleton
          variant="rectangular"
          sx={{ borderRadius: '12px', mt: 10 }}
          height={554}
        />
      ) : (
        <Box sx={{ mt: 10 }}>
          <Section>
            <DemandModifiersTable
              modifiers={modifiers}
              onEdit={handleEditModifier}
              onDelete={handleDeleteClick}
              onCreate={handleCreateModifier}
              canEdit={canWriteDemandModifiers}
              canDelete={canDeleteDemandModifiers}
              canCreate={canWriteDemandModifiers}
            />
          </Section>
        </Box>
      )}

      <DemandModifierModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveModifier}
        categories={categories}
        modifier={selectedModifier}
        isEditMode={!!selectedModifier}
        skuError={skuError}
        onClearSkuError={() => setSkuError('')}
      />

      <DeleteDemandModifierDialog
        open={isDeleteDialogOpen}
        onCancel={handleCloseDeleteDialog}
        onConfirm={handleDeleteConfirm}
        modifierName={modifierToDelete?.name || ''}
        isDeleting={isDeleting}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={7000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default DemandModifiersPage;
